import { notificationRead, notificationRemoves } from '@/services/notification/notification'
import { fetchNotificationCount, fetchNotifications, selectNotification } from '@/store/notification/notification.slice'
import { NOTIFICATION_TYPES } from '@/utils/constants'
import { useRequest } from 'ahooks'
import { Button, Checkbox, Empty, List, Radio, Row, Space, Typography } from 'antd'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import classnames from 'classnames'
import dayjs from 'dayjs'
import React, { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import styles from './index.module.less'
import { AppDispatch } from '@/store'

export default memo(function Notification() {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const notifications = useSelector(selectNotification).notifications
  const [filterValue, setFilterValue] = useState('all')
  const [checkedList, setCheckedList] = React.useState<number[]>([])
  const [indeterminate, setIndeterminate] = React.useState(false)
  const [checkAll, setCheckAll] = React.useState(false)
  const { run: remove } = useRequest(notificationRemoves, {
    manual: true,
    onSuccess: () => {
      dispatch(fetchNotifications())
      dispatch(fetchNotificationCount())
      setCheckedList([])
    }
  })
  const { run: read } = useRequest(notificationRead, {
    manual: true,
    onSuccess: () => {
      dispatch(fetchNotifications())
      dispatch(fetchNotificationCount())
    }
  })
  const filterNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      switch (filterValue) {
        case 'all':
          return true
        case 'unread':
          return !notification.read
        case 'assign':
          return notification.type === 'TASK'
        case 'invitation':
          return notification.type === 'INVITATION'
      }

      return true
    })
  }, [notifications, filterValue])

  useEffect(() => {
    const list = checkedList.filter((item) => filterNotifications.some((n) => n.id === item))
    onChange(list)
  }, [filterNotifications])

  const onChange = (list) => {
    setCheckedList(list as number[])
    setIndeterminate(!!list.length && list.length < filterNotifications.length)
    setCheckAll(!!filterNotifications.length && list.length === filterNotifications.length)
  }

  const onCheckAllChange = (event: CheckboxChangeEvent) => {
    const checked = event.target.checked
    setCheckedList(checked ? filterNotifications.map((item) => item.id) : [])
    setIndeterminate(false)
    setCheckAll(checked)
  }

  useEffect(() => {
    dispatch(fetchNotifications())
  }, [])

  return (
    <div className={classnames('py-3')}>
      <div className={classnames('container-xl px-3')}>
        <Row justify={'space-between'}>
          <Typography.Title level={4}>Notifications</Typography.Title>

          <Button onClick={() => navigate(-1)}>Close</Button>
        </Row>

        <Radio.Group
          value={filterValue}
          onChange={(event) => {
            const value = event.target.value
            setFilterValue(value)
          }}
        >
          <Radio.Button value='all'>ALL</Radio.Button>
          <Radio.Button value='unread'>Unread</Radio.Button>
          <Radio.Button value='assign'>Assigned to me</Radio.Button>
          <Radio.Button value='invitation'>invitations</Radio.Button>
        </Radio.Group>

        <div className={classnames('mt-2')}>
          <List
            size='large'
            header={
              <div>
                <Checkbox indeterminate={indeterminate} onChange={onCheckAllChange} checked={checkAll}>
                  Select all
                </Checkbox>

                {!!checkedList?.length && (
                  <Space size={16}>
                    <span>{checkedList.length} selected</span>

                    <Button
                      type='default'
                      onClick={() => {
                        remove(checkedList)
                      }}
                    >
                      Delete
                    </Button>

                    <Button
                      type='default'
                      onClick={() => {
                        read(checkedList)
                      }}
                    >
                      Mark as read
                    </Button>
                  </Space>
                )}
              </div>
            }
            bordered
          >
            {!!filterNotifications.length && (
              <Checkbox.Group className={'w100'} value={checkedList} onChange={onChange}>
                {filterNotifications.map((notification) => (
                  <List.Item key={notification.id} className={classnames({ [styles.readItem]: notification.read })}>
                    {!notification.read && <span className={styles.unreadIndicator} />}

                    <Space style={{ width: '50%' }}>
                      <Checkbox value={notification.id} />
                      <Link to={notification.value}>{notification.content}</Link>
                    </Space>
                    <span>{NOTIFICATION_TYPES[notification.type]}</span>
                    <span>{dayjs(notification.createTime).format('MMM D, YYYY')}</span>
                  </List.Item>
                ))}
              </Checkbox.Group>
            )}
            {!filterNotifications.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          </List>
        </div>
      </div>
    </div>
  )
})
